import My3DViewer from './components/My3DViewer'
import VolumeDisplay from './components/VolumeDisplay'
import { useState, useEffect, useRef } from 'react'
import { fetchModels, type ModelInfo } from './services/api'
import * as THREE from 'three'
import './App.css'
import logo from '/logo.png'

const DEBUG = import.meta.env.DEV;

function App() {
  const [sampleModels, setSampleModels] = useState<ModelInfo[]>([]);
  const [driveModels, setDriveModels] = useState<ModelInfo[]>([]);
  const [selectedModel, setSelectedModel] = useState<ModelInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [folderId, setFolderId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // States lifted from My3DViewer for the UI panel
  const [modelError, setModelError] = useState<string | null>(null);
  const [mtlLoaded, setMtlLoaded] = useState<boolean | null>(null);
  const [loadedTextures, setLoadedTextures] = useState<string[]>([]);
  const [loadedModel, setLoadedModel] = useState<THREE.Group | null>(null);
  const viewerRef = useRef<{ resetView: () => void }>(null);

  // Lighting states
  const [ambientColor, setAmbientColor] = useState('#ffffff');
  const [ambientIntensity, setAmbientIntensity] = useState(1.0);
  const [keyLightColor, setKeyLightColor] = useState('#ffffff');
  const [keyLightIntensity, setKeyLightIntensity] = useState(1.5);
  const [fillLightColor, setFillLightColor] = useState('#ffffff');
  const [fillLightIntensity, setFillLightIntensity] = useState(0.5);
  const [rimLightColor, setRimLightColor] = useState('#ffffff');
  const [rimLightIntensity, setRimLightIntensity] = useState(0.3);
  const [bgColor, setBgColor] = useState('#828282');

  // Debug: Track state changes
  useEffect(() => {
    if (DEBUG) console.log('Sample models:', sampleModels.length, 'Drive models:', driveModels.length);
  }, [sampleModels, driveModels]);

  useEffect(() => {
    console.log('Selected model changed:', selectedModel?.name || 'null');
  }, [selectedModel]);

  // Helper to extract folder ID from potential URL
  const extractFolderId = (input: string) => {
    const trimmed = input.trim();
    // Match pattern like .../folders/ID or ?id=ID
    const folderMatch = trimmed.match(/folders\/([a-zA-Z0-9_-]{25,})/);
    const idMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]{25,})/);
    
    return folderMatch ? folderMatch[1] : (idMatch ? idMatch[1] : trimmed);
  };

  // Load sample models from default folder
  async function loadSampleModels() {
    // Safety timeout: Ensure splash screen hides after 5s even if API fails/hangs
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    try {
      const defaultFolderId = import.meta.env.VITE_DEFAULT_FOLDER_ID;
      if (!defaultFolderId) {
        setLoading(false);
        clearTimeout(safetyTimeout);
        return;
      }

      if (DEBUG) console.log('Loading sample models from default folder');
      const modelList = await fetchModels(defaultFolderId);
      setSampleModels(modelList);
    } catch (err) {
      console.error('Failed to load sample models:', err);
    } finally {
      setLoading(false);
      clearTimeout(safetyTimeout);
    }
  }

  // Load user's Drive folder models
  async function loadDriveModels(id?: string) {
    try {
      if (DEBUG) console.log('Load drive models function called');
      setLoading(true);
      const targetInput = id || folderId;
      const targetId = extractFolderId(targetInput);

      if (!targetId) {
        setError('Please enter a folder ID or URL');
        setLoading(false);
        return;
      }

      if (DEBUG) console.log(`Attempting to load folder: ${targetId}`);

      const modelList = await fetchModels(targetId);
      if (DEBUG) console.log(`Received ${modelList.length} models from API`, modelList);
      setDriveModels(modelList);
      if (modelList.length === 0) {
        setError('No .obj models found in this folder');
        setSelectedModel(null);
      } else {
        setError(null);
        if (DEBUG) console.log('Setting selected model to:', modelList[0]);
        setSelectedModel(modelList[0]);
      }
    } catch (err) {
      console.error('Failed to load drive models:', err);
      setError('Failed to fetch models. Check folder URL/ID and permissions.');
      setDriveModels([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSampleModels();

    // Check for 'url' query parameter
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get('url');
    if (urlParam) {
      if (DEBUG) console.log('Auto-loading from URL param:', urlParam);
      setFolderId(urlParam);
      loadDriveModels(urlParam);
    }
  }, []);

  const handleFolderSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (DEBUG) console.log('Form submitted with folderId:', folderId);
    loadDriveModels();
  };

  // Use direct Google Drive URLs
  const objPath = selectedModel?.objUrl || '';
  const mtlFile = selectedModel?.relatedFiles?.find(f => f.type === 'mtl');
  const mtlPath = mtlFile?.url || '';

  // Display names
  const objName = selectedModel ? `${selectedModel.name}.obj` : '';
  const mtlName = mtlFile ? mtlFile.name : '';

  return (
    <div className="App">
      {loading && (
        <div className="app-splash">
          <div className="splash-logo-wrap">
            <img src={logo} alt="Logo" className="splash-logo" />
          </div>
          <p className="splash-text">Model Previewer</p>
        </div>
      )}

      <div className="side-panel-left">
        <div className="top-controls">
          <form onSubmit={handleFolderSubmit} className="folder-input-group">
            <input
              type="text"
              placeholder="Paste Drive URL or Folder ID"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Loading...' : 'Load Folder'}
            </button>
          </form>
          <ul className="instructions">
            <li>💡 Share folder with <code>figuro-service-account@figuro-480305.iam.gserviceaccount.com</code> (Viewer access) or set to <em>"Anyone with the link"</em>.</li>
            <li>💡 Include OBJ, MTL, and PNG files in your folder.</li>
            <li>💡 Large models are limited by your device's GPU memory and browser capabilities.</li>
          </ul>
        </div>

        {selectedModel && (
          <div className="loaded-files-panel">
            <div>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Loaded Files</h3>
              <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                <div>OBJ: ✓ {objName}</div>
                <div>
                  MTL: {mtlPath ? (
                    mtlLoaded === true ? `✓ ${mtlName}` :
                    mtlLoaded === false ? `✗ ${mtlName} (not found)` :
                    `⏳ ${mtlName}`
                  ) : 'None'}
                </div>
                <div>Textures: {loadedTextures.length > 0 ? loadedTextures.join(', ') : 'None'}</div>
              </div>
              
              <VolumeDisplay model={loadedModel} />

              {modelError && (
                <div style={{ color: '#ff6b6b', marginTop: '8px', fontSize: '12px' }}>
                  Error: {modelError}
                </div>
              )}
            </div>

            <div>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Background</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                  Color:
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  />
                </label>
              </div>
            </div>

            <div>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Lighting</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontSize: '11px' }}>Ambient</label>
                    <input type="color" value={ambientColor} onChange={(e) => setAmbientColor(e.target.value)} style={{ height: '18px', width: '30px' }} />
                  </div>
                  <input type="range" min="0" max="2" step="0.1" value={ambientIntensity} onChange={(e) => setAmbientIntensity(parseFloat(e.target.value))} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontSize: '11px' }}>Key Light</label>
                    <input type="color" value={keyLightColor} onChange={(e) => setKeyLightColor(e.target.value)} style={{ height: '18px', width: '30px' }} />
                  </div>
                  <input type="range" min="0" max="3" step="0.1" value={keyLightIntensity} onChange={(e) => setKeyLightIntensity(parseFloat(e.target.value))} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontSize: '11px' }}>Fill Light</label>
                    <input type="color" value={fillLightColor} onChange={(e) => setFillLightColor(e.target.value)} style={{ height: '18px', width: '30px' }} />
                  </div>
                  <input type="range" min="0" max="2" step="0.1" value={fillLightIntensity} onChange={(e) => setFillLightIntensity(parseFloat(e.target.value))} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontSize: '11px' }}>Rim Light</label>
                    <input type="color" value={rimLightColor} onChange={(e) => setRimLightColor(e.target.value)} style={{ height: '18px', width: '30px' }} />
                  </div>
                  <input type="range" min="0" max="2" step="0.1" value={rimLightIntensity} onChange={(e) => setRimLightIntensity(parseFloat(e.target.value))} />
                </div>
              </div>
            </div>

            <button
              onClick={() => viewerRef.current?.resetView()}
              style={{
                padding: '8px 12px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '12px',
                marginTop: '10px'
              }}
            >
              Reset View
            </button>
          </div>
        )}
      </div>

      <h1 className="viewer-title">
        <img src={logo} alt="Logo" className="title-logo" />
        Model Previewer
      </h1>

      <div className="controls-panel">
      <div className="model-list">
        <h3>Models ({sampleModels.length + driveModels.length})</h3>
        
        <input
          type="text"
          placeholder="Search models..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '0.8em',
            marginBottom: '10px',
            width: '100%',
            boxSizing: 'border-box'
          }}
        />

        {/* User-loaded models first */}
        {driveModels.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase())).length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <div style={{ fontSize: '0.8em', color: '#999', marginBottom: '8px', paddingLeft: '10px' }}>Loaded model folder</div>
            {driveModels
              .filter(model => model.name.toLowerCase().includes(searchQuery.toLowerCase()))
              .map(model => (
                <button
                  key={model.name}
                  onClick={() => setSelectedModel(model)}
                  className={selectedModel?.name === model.name ? 'active' : ''}
                  style={{ marginBottom: '6px', fontSize: '0.8em' }}
                >
                  {model.name}
                </button>
              ))}
          </div>
        )}

        {/* Default models second */}
        {sampleModels.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase())).length > 0 && (
          <div style={{ borderTop: driveModels.length > 0 ? '1px solid #444' : 'none', paddingTop: driveModels.length > 0 ? '10px' : '0' }}>
            <a
              href={`https://drive.google.com/drive/folders/${import.meta.env.VITE_DEFAULT_FOLDER_ID}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.8em', color: '#0ea5e9', marginBottom: '8px', paddingLeft: '10px', display: 'block', textDecoration: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Default Model Folder
            </a>
            {sampleModels
              .filter(model => model.name.toLowerCase().includes(searchQuery.toLowerCase()))
              .map(model => (
                <button
                  key={model.name}
                  onClick={() => setSelectedModel(model)}
                  className={selectedModel?.name === model.name ? 'active' : ''}
                  style={{ marginBottom: '6px', fontSize: '0.8em' }}
                >
                  {model.name}
                </button>
              ))}
          </div>
        )}

        {sampleModels.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && 
         driveModels.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
          <div style={{ padding: '10px', color: '#999', fontSize: '0.85em' }}>
            {searchQuery ? 'No matching models' : 'No models loaded yet.'}
          </div>
        )}
      </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Intro overlay — visible when no model selected, sits above the always-mounted viewer */}
      {!selectedModel && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          textAlign: 'center',
          background: '#1a1a1a',
          padding: '40px',
          zIndex: 10,
          pointerEvents: 'none',
        }}>
          <h2 style={{ fontSize: '2.5em', marginBottom: '20px' }}>3D Model Viewer</h2>
          <p style={{ fontSize: '1.2em', maxWidth: '600px', marginBottom: '40px', lineHeight: '1.6' }}>
            Load 3D models from Google Drive to preview them in real-time. Supports OBJ + MTL materials and PNG textures.
          </p>
          <div style={{ marginBottom: '40px', fontSize: '1em', maxWidth: '600px' }}>
            <h3>How it works:</h3>
            <ol style={{ textAlign: 'left', display: 'inline-block' }}>
              <li>Create a Google Drive folder with OBJ, MTL, and PNG texture files</li>
              <li>Share the folder with: <br/><code style={{ fontSize: '0.9em', background: 'rgba(255,255,255,0.1)', padding: '2px 5px', borderRadius: '3px' }}>figuro-service-account@figuro-480305.iam.gserviceaccount.com</code></li>
              <li>Paste the folder URL or ID above</li>
              <li>Select a model to preview it in the viewer</li>
            </ol>
          </div>
        </div>
      )}

      {/* Viewer always mounted to preserve canvas/lighting state */}
      <My3DViewer
        ref={viewerRef}
        objPath={objPath}
        objName={objName}
        mtlPath={mtlPath}
        mtlName={mtlName}
        textureUrls={selectedModel?.relatedFiles || []}
        onLoadError={setModelError}
        onMtlStatus={setMtlLoaded}
        onTexturesLoaded={setLoadedTextures}
        onModelLoaded={setLoadedModel}
        ambientColor={ambientColor}
        ambientIntensity={ambientIntensity}
        keyLightColor={keyLightColor}
        keyLightIntensity={keyLightIntensity}
        fillLightColor={fillLightColor}
        fillLightIntensity={fillLightIntensity}
        rimLightColor={rimLightColor}
        rimLightIntensity={rimLightIntensity}
        bgColor={bgColor}
      />
    </div>
  )
}

export default App
